(() => {
  const initialize = (root = document) => {
    root.querySelectorAll('[data-nf-paths]').forEach(section => {
      if (section.dataset.initialized) return;
      section.dataset.initialized = 'true';
      const track = section.querySelector('.nf-paths__track');
      const cards = [...section.querySelectorAll('.nf-paths__card')];
      const navigation = section.querySelector('.nf-paths__navigation');
      const mobileScroll = matchMedia('(max-width: 749px)');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const effects = section.dataset.effects === 'true';
      const abort = new AbortController();
      const signal = abort.signal;
      if (!cards.length) return;
      let frame = 0, drag = null, suppressClickUntil = 0, reveal;
      const hoverCleanups = [];
      const maximum = () => mobileScroll.matches ? Math.max(0, track.scrollWidth - track.clientWidth) : 0;
      const positions = () => {
        const edge = track.getBoundingClientRect().left;
        return [...new Set(cards.map(card => Math.min(maximum(), Math.max(0, card.getBoundingClientRect().left - edge + track.scrollLeft - 2))))];
      };
      const moveTo = left => {
        if (mobileScroll.matches) track.scrollTo({left, behavior:reduced.matches ? 'instant' : 'smooth'});
      };
      const step = direction => {
        const stops = positions();
        moveTo(direction > 0 ? stops.find(left => left > track.scrollLeft + 4) ?? maximum() : stops.reverse().find(left => left < track.scrollLeft - 4) ?? 0);
      };
      const update = () => {
        if (!mobileScroll.matches) { finishDrag(); track.scrollLeft = 0; suppressClickUntil = 0; }
        const max = maximum();
        navigation.hidden = max < 5;
        track.tabIndex = max < 5 ? -1 : 0;
        section.style.setProperty('--paths-progress', max ? Math.min(1,Math.max(0,track.scrollLeft/max)) : 0);
        frame = 0;
      };
      const schedule = () => {if (!frame) frame = requestAnimationFrame(update);};
      mobileScroll.addEventListener('change', schedule, {signal});
      track.addEventListener('scroll', schedule, {passive:true,signal});
      track.addEventListener('dragstart', event => event.preventDefault(), {signal});
      track.addEventListener('pointerdown', event => {
        if (maximum() < 5 || event.pointerType === 'touch' || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        drag = {id:event.pointerId,x:event.clientX,y:event.clientY,left:track.scrollLeft,moved:false};
      }, {signal});
      track.addEventListener('pointermove', event => {
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!drag.moved && Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) {
          drag.moved = true;
          track.setPointerCapture(event.pointerId);
          track.classList.add('is-dragging');
        }
        if (drag.moved) {
          event.preventDefault();
          track.scrollLeft = drag.left - dx;
        }
      }, {signal});
      const finishDrag = () => {
        if (!drag) return;
        const {id,moved} = drag;
        drag = null;
        if (track.hasPointerCapture(id)) track.releasePointerCapture(id);
        track.classList.remove('is-dragging');
        if (moved) {
          suppressClickUntil = performance.now() + 400;
          const stops = positions();
          const nearest = stops.reduce((best,left) => Math.abs(left-track.scrollLeft) < Math.abs(best-track.scrollLeft) ? left : best, stops[0]);
          moveTo(nearest);
        }
      };
      track.addEventListener('pointerup',finishDrag,{signal});
      track.addEventListener('pointercancel',finishDrag,{signal});
      track.addEventListener('lostpointercapture',finishDrag,{signal});
      track.addEventListener('pointerleave',() => {if (drag && !drag.moved) drag=null;},{signal});
      track.addEventListener('click',event => {
        if (event.detail > 0 && performance.now() < suppressClickUntil) {event.preventDefault();event.stopImmediatePropagation();}
      },{capture:true,signal});
      track.addEventListener('wheel',event => {
        if (!mobileScroll.matches || !event.shiftKey || event.deltaX || !event.deltaY) return;
        const delta = event.deltaY * (event.deltaMode === 1 ? 20 : event.deltaMode === 2 ? track.clientWidth : 1);
        if ((delta > 0 && track.scrollLeft < maximum()-1) || (delta < 0 && track.scrollLeft > 1)) {event.preventDefault();track.scrollLeft += delta;}
      },{passive:false,signal});
      track.addEventListener('keydown',event => {
        if (maximum() < 5 || event.target !== track || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') moveTo(event.key === 'Home' ? 0 : maximum());
        else step(event.key === 'ArrowRight' ? 1 : -1);
      },{signal});
      if ('IntersectionObserver' in window && effects && !reduced.matches) {
        reveal = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {section.classList.add('is-revealed');reveal.disconnect();}
        },{threshold:.12});
        reveal.observe(section);
      }
      if (effects && matchMedia('(hover:hover) and (pointer:fine)').matches) {
        cards.forEach(card => {
          let hoverFrame = 0, pointer = null, box = null;
          const reset = () => {
            cancelAnimationFrame(hoverFrame);hoverFrame=0;pointer=null;box=null;
            ['--path-x','--path-y','--path-light-x','--path-light-y'].forEach(key=>card.style.removeProperty(key));
          };
          const paint = () => {
            hoverFrame=0;
            if (!pointer || reduced.matches || drag?.moved) return;
            box ||= card.getBoundingClientRect();
            const x=Math.max(0,Math.min(1,(pointer.x-box.left)/box.width))-.5;
            const y=Math.max(0,Math.min(1,(pointer.y-box.top)/box.height))-.5;
            card.style.setProperty('--path-x',x*11+'px');card.style.setProperty('--path-y',y*9+'px');
            card.style.setProperty('--path-light-x',x*25+'px');card.style.setProperty('--path-light-y',y*15+'px');
          };
          card.addEventListener('pointerenter',()=>{box=card.getBoundingClientRect();},{passive:true,signal});
          card.addEventListener('pointermove',event => {
            if (reduced.matches || drag?.moved) return;
            pointer={x:event.clientX,y:event.clientY};
            if (!hoverFrame) hoverFrame=requestAnimationFrame(paint);
          },{passive:true,signal});
          card.addEventListener('pointerleave',reset,{signal});
          track.addEventListener('scroll',()=>{box=null;},{passive:true,signal});
          hoverCleanups.push(reset);
        });
      }
      const resize = 'ResizeObserver' in window ? new ResizeObserver(()=>{hoverCleanups.forEach(reset=>reset());schedule();}) : null;
      resize?.observe(track);
      document.addEventListener('shopify:section:unload',event => {
        if (!event.target.contains(section)) return;
        abort.abort();resize?.disconnect();reveal?.disconnect();cancelAnimationFrame(frame);hoverCleanups.forEach(reset=>reset());
      },{signal});
      section.addEventListener('shopify:block:select',event => {
        const card=cards.find(item=>item===event.target||item.contains(event.target));
        if (card) moveTo(card.getBoundingClientRect().left-track.getBoundingClientRect().left+track.scrollLeft-2);
      },{signal});
      update();
    });
  };
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',()=>initialize(),{once:true});
  else initialize();
  if (!window.nfHomeCategoryEditorBound) {
    document.addEventListener('shopify:section:load',event=>initialize(event.target));
    window.nfHomeCategoryEditorBound=true;
  }
})();
